// apps/web/src/pages/signup.tsx — Phase 7 plan 07-06 (D-07-03 + W11 fix).
//
// Public self-service signup form at /signup. Renders on the bare
// domain (gastio.app/signup) AND on a tenant subdomain
// (acme-bistro.gastio.app/signup). The DE/EN pill switches the
// bundle language; the form fields submit a GraphQL
// `requestSignup` mutation. On SLUG_TAKEN the response surfaces
// `extensions.suggestions` which the form renders as clickable
// chips that auto-fill the slug input (D-07-03).
//
// Fields:
//   - slug (lowercase a-z 0-9 dashes, 3-40 chars; D-07-01)
//   - restaurantName (1..120 chars)
//   - email (RFC 5322)
//   - addressLine (1..200 chars)
//   - postalCode (4..10 digits; DE/EN mirror — same regex per the
//     server-side SignupInput schema)
//   - city (1..100 chars)
//   - consent (required checkbox)
// On success: renders a "check your email" copy from i18n.

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { gqlRequest } from "../lib/api";
import { useBaseDomain } from "../lib/base-domain-context";

const REQUEST_SIGNUP_MUTATION = /* GraphQL */ `
  mutation SignupRequest($input: SignupInput!) {
    requestSignup(input: $input) {
      slug
      email
      verificationUrl
      expiresAt
    }
  }
`;

interface RequestSignupResponse {
  requestSignup: {
    slug: string;
    email: string;
    verificationUrl: string;
    expiresAt: string;
  };
}

const SLUG_RE = /^[a-z0-9-]{3,40}$/;
const POSTAL_RE = /^\d{4,10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  slug: string;
  restaurantName: string;
  email: string;
  addressLine: string;
  postalCode: string;
  city: string;
  consent: boolean;
}

const EMPTY_FORM: FormState = {
  slug: "",
  restaurantName: "",
  email: "",
  addressLine: "",
  postalCode: "",
  city: "",
  consent: false,
};

const CODE_TO_I18N: Record<string, string> = {
  SLUG_TAKEN: "signup.slugTaken",
  RESERVED_SLUG: "signup.slugTaken",
  INVALID_INPUT: "signup.invalidInput",
};

export function SignupPage(): JSX.Element {
  const { t, i18n: i18next } = useTranslation();
  // Phase 8 plan 08-01 (D-08-01): the build-time BASE_DOMAIN
  // constant flows into the slug helper text via the
  // `{{baseDomain}}` placeholder. Read from context (mounted by
  // main.tsx) so a future Plan could swap the source for an
  // env-derived value without touching this page.
  const baseDomain = useBaseDomain();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});

  const isEnglish = i18next.language.startsWith("en");

  const mutation = useMutation({
    mutationFn: () =>
      gqlRequest<RequestSignupResponse>(REQUEST_SIGNUP_MUTATION, {
        input: {
          slug: form.slug,
          restaurantName: form.restaurantName,
          email: form.email,
          addressLine: form.addressLine,
          postalCode: form.postalCode,
          city: form.city,
          consentText: `signup:${new Date().toISOString()}`,
          consentAt: new Date().toISOString(),
        },
      }),
    onSuccess: (res) => {
      setErrorKey(null);
      setSuggestions([]);
      setSuccessEmail(res.requestSignup.email);
    },
    onError: (err: unknown) => {
      if (err instanceof ClientError) {
        const ext = err.response?.errors?.[0]?.extensions as
          { code?: string; suggestions?: unknown } | undefined;
        const code = ext?.code ?? "generic";
        const codeStr = typeof code === "string" ? code : "generic";
        setErrorKey(CODE_TO_I18N[codeStr] ?? "signup.genericError");
        const sugg = ext?.suggestions;
        setSuggestions(Array.isArray(sugg) ? (sugg as string[]) : []);
        return;
      }
      setErrorKey("signup.genericError");
      setSuggestions([]);
    },
  });

  function validate(): Partial<Record<keyof FormState, string>> {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!SLUG_RE.test(form.slug)) {
      errs.slug = "signup.invalidInput";
    }
    if (form.restaurantName.trim().length === 0) {
      errs.restaurantName = "signup.invalidInput";
    }
    if (!EMAIL_RE.test(form.email)) {
      errs.email = "signup.invalidInput";
    }
    if (form.addressLine.trim().length === 0) {
      errs.addressLine = "signup.invalidInput";
    }
    if (!POSTAL_RE.test(form.postalCode)) {
      errs.postalCode = "signup.invalidInput";
    }
    if (form.city.trim().length === 0) {
      errs.city = "signup.invalidInput";
    }
    if (!form.consent) {
      errs.consent = "signup.invalidInput";
    }
    return errs;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    mutation.mutate();
  }

  if (successEmail) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex flex-col items-center justify-center px-6 py-12"
        data-testid="signup-success"
      >
        <div
          className="card"
          style={{
            borderRadius: 14,
            width: "min(440px, 100%)",
            padding: "28px 24px",
          }}
        >
          <h1 className="font-heading text-[22px] font-semibold mb-3">
            {t("signup.title")}
          </h1>
          <p className="text-sm text-text/80">
            {t("signup.checkEmail", { email: successEmail })}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex flex-col items-center px-4 py-8"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      <div
        className="card"
        style={{
          borderRadius: 14,
          width: "min(440px, 100%)",
          padding: "28px 24px",
        }}
        data-testid="signup-form"
      >
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            aria-label={isEnglish ? "Change language" : "Sprache wechseln"}
            data-testid="lang-toggle"
            onClick={() => {
              const next = isEnglish ? "de" : "en";
              void i18next.changeLanguage(next);
              try {
                window.localStorage.setItem("gastio.lang", next);
              } catch {
                /* localStorage may be unavailable; toggle still works in-memory */
              }
            }}
            className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full border border-divider font-heading text-sm hover:bg-surface"
          >
            {isEnglish ? "EN" : "DE"}
          </button>
        </div>
        <h1
          className="font-heading text-[22px] font-semibold mb-1"
          data-testid="signup-title"
        >
          {t("signup.title")}
        </h1>
        <p className="text-sm text-text/70 mb-5">{t("signup.subtitle")}</p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm">{t("signup.slug")}</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) =>
                setForm((s) => ({ ...s, slug: e.target.value.toLowerCase() }))
              }
              className="field"
              data-testid="slug-input"
              autoComplete="off"
              spellCheck={false}
            />
            <span className="text-[11px] text-text/60">
              {t("signup.slugHint", {
                slug: form.slug || "acme-bistro",
                baseDomain,
              })}
            </span>
            {suggestions.length > 0 ? (
              <div
                className="flex flex-wrap gap-2 mt-1"
                data-testid="slug-suggestions"
              >
                <span className="text-[11px] text-text/70 self-center">
                  {t("signup.slugSuggestions")}:
                </span>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="inline-flex items-center px-2 py-1 rounded-full bg-surface text-xs hover:bg-accent/10"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, slug: s }));
                      setSuggestions([]);
                    }}
                    data-testid={`slug-suggestion-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            ) : null}
            {fieldErrors.slug ? (
              <span
                className="text-[11px] text-red-700"
                role="alert"
                data-testid="slug-error"
              >
                {t(fieldErrors.slug)}
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm">
              {t("signup.restaurantName")}
            </span>
            <input
              type="text"
              value={form.restaurantName}
              onChange={(e) =>
                setForm((s) => ({ ...s, restaurantName: e.target.value }))
              }
              className="field"
              data-testid="restaurant-name-input"
              autoComplete="organization"
            />
            {fieldErrors.restaurantName ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t(fieldErrors.restaurantName)}
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-heading text-sm">{t("signup.email")}</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((s) => ({ ...s, email: e.target.value }))
              }
              className="field"
              data-testid="email-input"
              autoComplete="email"
            />
            {fieldErrors.email ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t(fieldErrors.email)}
              </span>
            ) : null}
          </label>
          <fieldset className="flex flex-col gap-2 border border-divider rounded-md p-3">
            <legend className="font-heading text-sm px-1">
              {t("signup.address")}
            </legend>
            <input
              type="text"
              placeholder={t("signup.address")}
              value={form.addressLine}
              onChange={(e) =>
                setForm((s) => ({ ...s, addressLine: e.target.value }))
              }
              className="field"
              data-testid="address-input"
              autoComplete="street-address"
            />
            {fieldErrors.addressLine ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t(fieldErrors.addressLine)}
              </span>
            ) : null}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t("signup.postalCode")}
                value={form.postalCode}
                onChange={(e) =>
                  setForm((s) => ({ ...s, postalCode: e.target.value }))
                }
                className="field"
                data-testid="postal-input"
                autoComplete="postal-code"
                style={{ maxWidth: 120 }}
              />
              <input
                type="text"
                placeholder={t("signup.city")}
                value={form.city}
                onChange={(e) =>
                  setForm((s) => ({ ...s, city: e.target.value }))
                }
                className="field"
                data-testid="city-input"
                autoComplete="address-level2"
              />
            </div>
            {fieldErrors.postalCode ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t(fieldErrors.postalCode)}
              </span>
            ) : null}
            {fieldErrors.city ? (
              <span className="text-[11px] text-red-700" role="alert">
                {t(fieldErrors.city)}
              </span>
            ) : null}
          </fieldset>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) =>
                setForm((s) => ({ ...s, consent: e.target.checked }))
              }
              data-testid="consent-input"
              className="mt-1"
            />
            <span>{t("signup.consent")}</span>
          </label>
          {fieldErrors.consent ? (
            <span className="text-[11px] text-red-700" role="alert">
              {t(fieldErrors.consent)}
            </span>
          ) : null}
          {errorKey ? (
            <p
              className="text-[11px] text-red-700"
              role="alert"
              data-testid="signup-error"
            >
              {t(errorKey)}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary btn-block"
            data-testid="signup-submit"
          >
            {mutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" aria-hidden />
                {t("signup.submitting")}
              </span>
            ) : (
              t("signup.submit")
            )}
          </button>
        </form>
      </div>
    </main>
  );
}
